import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUS_VARIANT = { verified: "default", configured: "secondary", not_configured: "outline", invalid: "destructive", unavailable: "destructive", unconfigured: "outline" };
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(4)}`);

export default function AdminAI() {
  const [providers, setProviders] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/ai/providers").then((r) => setProviders(r.data)),
      api.get("/admin/ai/usage").then((r) => setUsage(r.data)),
    ]).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-ai-page">
      <PageHeader title="AI Providers & Usage" subtitle="Global, read-only operational view across all tenants. No API keys or secrets are ever shown." />
      {loading ? (
        <p className="font-mono text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-8">
          {/* Aggregate usage */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-sm p-5" data-testid="admin-ai-total-requests"><p className="text-3xl font-heading tabular-nums">{usage?.totals?.requests ?? 0}</p><p className="text-xs text-muted-foreground mt-1">Total AI requests</p></div>
            <div className="bg-card border border-border rounded-sm p-5" data-testid="admin-ai-total-cost"><p className="text-3xl font-heading tabular-nums">{money(usage?.totals?.estimated_cost)}</p><p className="text-xs text-muted-foreground mt-1">Estimated cost</p></div>
            <div className="bg-card border border-border rounded-sm p-5" data-testid="admin-ai-orgs"><p className="text-3xl font-heading tabular-nums">{usage?.totals?.organizations_with_usage ?? 0}</p><p className="text-xs text-muted-foreground mt-1">Orgs with usage</p></div>
            <div className="bg-card border border-border rounded-sm p-5" data-testid="admin-ai-providers-count"><p className="text-3xl font-heading tabular-nums">{providers.length}</p><p className="text-xs text-muted-foreground mt-1">Configured providers</p></div>
          </div>

          {/* Usage by capability */}
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="admin-ai-by-capability">
            <div className="px-4 py-3 border-b border-border text-sm font-medium">Usage by capability</div>
            <Table>
              <TableHeader><TableRow><TableHead>Capability</TableHead><TableHead>Requests</TableHead><TableHead>Est. cost</TableHead></TableRow></TableHeader>
              <TableBody>{(usage?.by_capability || []).map((c) => (<TableRow key={c.capability}><TableCell><Badge variant="outline" className="rounded-sm text-[10px]">{c.capability}</Badge></TableCell><TableCell className="tabular-nums">{c.requests}</TableCell><TableCell className="font-mono text-xs">{money(c.estimated_cost)}</TableCell></TableRow>))}</TableBody>
            </Table>
          </div>

          {/* Per-org usage */}
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="admin-ai-usage-by-org">
            <div className="px-4 py-3 border-b border-border text-sm font-medium">Usage by organization</div>
            <Table>
              <TableHeader><TableRow><TableHead>Organization</TableHead><TableHead>Requests</TableHead><TableHead>Failed</TableHead><TableHead>Avg latency</TableHead><TableHead>Est. cost</TableHead></TableRow></TableHeader>
              <TableBody>
                {(usage?.organizations || []).map((o) => (
                  <TableRow key={o.org_id} data-testid={`admin-ai-org-${o.org_id}`}>
                    <TableCell className="text-sm">{o.org_name || <span className="font-mono text-xs text-muted-foreground">{o.org_id}</span>}</TableCell>
                    <TableCell className="tabular-nums">{o.requests}</TableCell>
                    <TableCell className={`tabular-nums ${o.failed ? "text-destructive" : ""}`}>{o.failed}</TableCell>
                    <TableCell className="font-mono text-xs">{o.avg_latency_ms}ms</TableCell>
                    <TableCell className="font-mono text-xs">{money(o.estimated_cost)}</TableCell>
                  </TableRow>
                ))}
                {(!usage?.organizations || usage.organizations.length === 0) && (<TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No AI usage recorded yet.</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>

          {/* Configured providers */}
          <div className="bg-card border border-border rounded-sm overflow-x-auto" data-testid="admin-ai-providers-table">
            <div className="px-4 py-3 border-b border-border text-sm font-medium">Configured providers (all tenants)</div>
            <Table>
              <TableHeader><TableRow><TableHead>Organization</TableHead><TableHead>Provider</TableHead><TableHead>Enabled</TableHead><TableHead>Credentials</TableHead><TableHead>Status</TableHead><TableHead>Health</TableHead><TableHead>Last check</TableHead></TableRow></TableHeader>
              <TableBody>
                {providers.map((p, i) => (
                  <TableRow key={i} data-testid={`admin-ai-provider-row-${i}`}>
                    <TableCell className="text-sm">{p.org_name || <span className="font-mono text-xs text-muted-foreground">{p.org_id}</span>}</TableCell>
                    <TableCell className="font-mono text-xs">{p.provider_id}</TableCell>
                    <TableCell>{p.enabled ? "Yes" : "No"}</TableCell>
                    <TableCell>{p.has_credentials ? <Badge variant="secondary" className="rounded-sm text-[10px]">configured</Badge> : "—"}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[p.status] || "outline"} className="rounded-sm text-[10px] uppercase">{p.status}</Badge></TableCell>
                    <TableCell className="text-xs">{p.health}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.last_check ? new Date(p.last_check).toLocaleString() : "—"}</TableCell>
                  </TableRow>
                ))}
                {providers.length === 0 && (<TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">No providers configured by any organization yet.</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground italic">Read-only. Platform administrators can never view API keys, decrypted secrets or impersonate an organization's credentials. Costs are estimated.</p>
        </div>
      )}
    </div>
  );
}
